// Pure, framework-free helpers for the retention-offer-limit gating logic
// used by the Retention icon on the View Agreement page. Extracted so the
// gating rules can be unit tested without mounting the full AgreementReturn
// component.

export interface RetentionOfferLimitConfig {
  retentionOfferLimitEnabled: boolean;
  maxRetentionOffersPerAgreement: number;
}

export interface RetentionOfferLimitState extends RetentionOfferLimitConfig {
  retentionOfferCount: number;
}

export interface RetentionOfferLimitData {
  retentionIconHidden: boolean;
  retentionLimitReached: boolean;
  retentionLimitTooltip: string;
}

// the activity types that count toward the per-agreement
// retention offer limit. Keep this list in sync with the backend.
export const RETENTION_OFFER_ACTIVITY_TYPES = [
  'AEPOS',
  'AEXTNS',
  'ARAS',
  'ATAS',
  'AGMRETSWS',
  'AGMRETSWP',
  'AGMRETSS',
  'AGMXNGS',
  'MS',
  'NLNS',
];

export const isCountedRetentionOfferType = (
  activityType: string | null | undefined
): boolean => {
  return (
    !!activityType && RETENTION_OFFER_ACTIVITY_TYPES.includes(activityType)
  );
};

// Backend now returns the raw "agreementActivities" ref_code array (the
// retention-offer activity types recorded across the agreement's exchange
// chain) instead of a pre-computed count; derive the count client-side.
export const getRetentionOfferCount = (
  agreementActivities: string[] | null | undefined
): number => {
  return Array.isArray(agreementActivities) ? agreementActivities.length : 0;
};

// Icon should not render at all when the store has the limit feature enabled
// but has configured a max of 0 (i.e. retention offers are fully disabled).
export const isRetentionIconHidden = ({
  retentionOfferLimitEnabled,
  maxRetentionOffersPerAgreement,
}: RetentionOfferLimitConfig): boolean => {
  return retentionOfferLimitEnabled && maxRetentionOffersPerAgreement === 0;
};

// Icon should render but be disabled (blurred/no-op click) once the
// agreement's existing retention-offer count has reached the configured max.
export const isRetentionLimitReached = ({
  retentionOfferLimitEnabled,
  maxRetentionOffersPerAgreement,
  retentionOfferCount,
}: RetentionOfferLimitState): boolean => {
  return (
    retentionOfferLimitEnabled &&
    maxRetentionOffersPerAgreement > 0 &&
    retentionOfferCount >= maxRetentionOffersPerAgreement
  );
};

export const getRetentionLimitTooltip = (
  maxRetentionOffersPerAgreement: number
): string => {
  return maxRetentionOffersPerAgreement === 1
    ? 'Retention offer has already been applied to this agreement. Only 1 offer is allowed per agreement.'
    : `Maximum retention offers (${maxRetentionOffersPerAgreement}) have been applied to this agreement.`;
};

// Single entry point for the Retention icon: combines the 4 pure helpers
// above into the one shape the UI needs. The individual helpers stay
// exported for direct reuse/unit testing.
export const getRetentionOfferLimitData = (
  config: RetentionOfferLimitConfig,
  agreementActivities: string[] | null | undefined
): RetentionOfferLimitData => {
  const retentionOfferCount = getRetentionOfferCount(agreementActivities);

  return {
    retentionIconHidden: isRetentionIconHidden(config),
    retentionLimitReached: isRetentionLimitReached({
      ...config,
      retentionOfferCount,
    }),
    retentionLimitTooltip: getRetentionLimitTooltip(
      config.maxRetentionOffersPerAgreement
    ),
  };
};
